import React from 'react';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const GuidedTissueBone = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Guided Tissue Bone Regeneration Fort Worth TX | GTR Surgery | Fort Worth Dental</title>
        <meta name="description" content="Guided tissue and bone regeneration (GTR) in Fort Worth TX. Advanced periodontal surgery to regenerate lost bone and tissue. Call (817) 985-3337." />
        <meta name="keywords" content="guided tissue regeneration fort worth, GTR fort worth tx, bone regeneration, periodontal regeneration, tissue regeneration" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/images/services/dentist-examines.jpg)`
        }}
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-center text-center">
          <div className="text-white max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Guided Tissue & Bone Regeneration in Fort Worth, TX</h1>
            <p className="text-lg sm:text-xl">Advanced Periodontal Regeneration</p>
            <p className="text-sm mt-4 opacity-90">Bone Regeneration | Tissue Regeneration | Periodontal Surgery</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          
          {/* What Is Bone Regeneration */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/guided-tissue-new-1.jpg" 
                  alt="Guided bone regeneration Fort Worth TX - Advanced periodontal procedure to restore lost jawbone density" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">What Is Bone Regeneration?</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Bone grafting regenerates bone using bone or bone substitute material. New bone will grow from the bone material. In the process of successfully grafting bone, it is possible to restore the ridge of a patient's jaw to its natural shape and contour. This bone restoration serves as a natural foundation for strong teeth and allows for eventual dental implants. This restoration can potentially save teeth as well. Bone grafting is performed when an area of the jaw is lacking in bone. This can be a problem for those considering dental implants. It can also be a problem for teeth that are in danger because the bone has deteriorated.
                </p>
              </div>
            </div>
          </section>

          {/* Three Types of Bone Graft Procedures */}
          <section className="mb-16 bg-teal-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">
              Three Types Of Bone Graft Procedures
            </h2>
            
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">1. Autogenous Bone Grafts</h3>
                <p className="text-gray-700 leading-relaxed">
                  This technique uses bone that is removed from one location on the patient and relocated at the location where it is needed. This bone is sometimes referred to as autografts. This material may be harvested from the chin, the jaw, the hip bone, the shin bone or from the back of the skull. Autogenous bone grafts are preferably derived from the jaw since this bone has the same type of bone material and is in the same location as the location where the bone graft is needed.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">2. Allograft Bone Grafts</h3>
                <p className="text-gray-700 leading-relaxed">
                  This bone graft procedure uses bone from a cadaver. This bone has been carefully prepared and processed by a bone bank. The advantage of an allograft is the fact that it does not require a second procedure for harvesting bone tissue from the patient. However, it does carry a certain level of risk that the bone material may be rejected or not integrated with the jaw. There are also disadvantages in that there is a lack of mineral and proteins that encourage bone growth and development.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">3. Xenograft</h3>
                <p className="text-gray-700 leading-relaxed">
                  This bone graft process harvests bone from an animal. This is typically bovine or cow bone. These materials can be used for humans as a substitute for bone. Like the allograft, the xenograft procedure does not require an additional procedure to harvest bone. However, there is also the risk of bone rejection and/or poor bone development.
                </p>
              </div>
            </div>
          </section>

          {/* What Is The Process */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  What Is The Process Of Guided Bone Tissue Regeneration?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  During the guided bone and tissue regeneration procedure, your Fort Worth dentist or periodontist will place a membrane over the area where bone needs to regenerate. This membrane acts as a barrier, preventing soft tissue from growing into the space where bone should form. The membrane guides the growth of new bone and tissue.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  The membrane may be resorbable (dissolves on its own) or non-resorbable (requires removal in a second procedure). Under the protection of this membrane, bone graft material is placed to provide a scaffold for new bone growth. Over several months, your body's natural healing process forms new bone in the area.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  This technique is particularly useful for patients who have experienced bone loss due to periodontal disease, tooth loss, or trauma. It creates the foundation needed for successful dental implant placement and helps restore the natural contours of the jaw.
                </p>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/guided-tissue-new-2.jpg" 
                  alt="Guided tissue regeneration process - Rebuilding bone and gum tissue for dental implant support" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288-3.png'}
                />
              </div>
            </div>
          </section>

          {/* Benefits */}
          <section className="mb-16 bg-gradient-to-br from-purple-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Benefits of Guided Tissue & Bone Regeneration</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Regenerates lost bone and tissue caused by periodontal disease</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Saves teeth that might otherwise be lost</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Creates ideal foundation for dental implants</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Restores natural jaw contours and facial structure</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Minimally invasive technique with predictable results</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Improves long-term oral health and stability</span>
              </li>
            </ul>
          </section>

          {/* Why Choose Fort Worth Dental */}
          <section className="mb-16 bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Why Choose Fort Worth Dental for Guided Tissue & Bone Regeneration?
            </h2>
            <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto mb-6">
              At Fort Worth Dental, we serve patients throughout Fort Worth, Aledo, Benbrook, and surrounding areas. 
              Our experienced periodontal team specializes in advanced regenerative techniques for optimal outcomes.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">30+</div>
                <p className="font-semibold">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">★★★★★</div>
                <p className="font-semibold">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">✓</div>
                <p className="font-semibold">Advanced Regeneration</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Schedule Your Consultation</h2>
            <p className="text-xl mb-8">Contact Fort Worth Dental to learn more about guided tissue & bone regeneration in Fort Worth, TX</p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a
                href="tel:8179853337"
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100"
              >
                Call (817) 985-3337
              </a>
              <a
                href="/#book-appointment"
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Book Appointment
              </a>
            </div>
          </section>

          {/* Appointment Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Schedule Your Appointment in Fort Worth
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Serving Fort Worth, Aledo, Benbrook, and surrounding communities. Call us today or fill out the form below.
              </p>
              <AppointmentForm />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default GuidedTissueBone;